import { Fragment, useCallback } from 'react';
import styled from 'styled-components';
import { colors, smallShadow } from 'ui/theme';
import { TableCell } from 'ui/tableCell';
import { Checkbox } from 'ui/checkboxV2/Checkbox';
import { noop } from 'utils/noop';

export type TableRowsColumn = Record<string, unknown>;

type Props = {
  columns: TableRowsColumn[];
  idKey?: string; // used to enable key configuration for datasets without `id` fields
  rows: any[];
  isLoading?: boolean;
  onSelectRow?: (row: Record<string, any>) => void;
  selectedRows?: any[];
  isCollapsible?: boolean;
  /**
   * when this prop is enabled each row in the table starts with a checkbox cell
   * and the defined cells are moved over one column to the right.
   * You **must** also enable the `hasCheckboxColumn` in the `TableHeader` component,
   * otherwise the columns will not be properly aligned.
   */
  hasCheckboxColumn?: boolean;
  /**
   * when this prop is enabled along with `hasCheckboxColumn`,
   * the table will limit the number of selectable checkboxes to the value defined here.
   */
  maxSelectedRows?: number;
};

type StyledRowProps = { isSelected: boolean };
const StyledRow = styled.div<StyledRowProps>`
  display: table-row;
  &:nth-child(even) {
    background-color: ${colors.grey1};
  }
  &:nth-child(odd) {
    background-color: ${colors.white};
  }
`;

const StyledChildrenRow = styled.div<StyledRowProps>`
  display: table-row;
  ${smallShadow}
  background: white;
`;

export const TableRows = ({
  columns = [],
  idKey,
  rows = [],
  selectedRows = [],
  onSelectRow = noop,
  isCollapsible = false,
  hasCheckboxColumn = false,
  maxSelectedRows,
  ...props
}: Props) => {
  /**
   * right now, the default behavior of the `onSelectRow` function is that it is invoked
   * whenever any part of the row or one of its cells is clicked. This is not the desired
   * behavior when the row has a checkbox (when `hasCheckboxColumn` is enabled), as we only
   * want that function to fire when the checkbox is clicked.
   */
  const onClick = useCallback(
    (row: Record<string, any>) => {
      return hasCheckboxColumn ? noop : onSelectRow(row);
    },
    [hasCheckboxColumn, onSelectRow],
  );

  return (
    <>
      {rows.map((row) => {
        const id = row[idKey || ''] || row.id;
        const key = row?.key || `row-${id}`;
        const isSelected = selectedRows.includes(row) || selectedRows.includes(id);
        const { isExpanded, childrenRows = [] }: { isExpanded?: boolean; childrenRows: any[] } =
          row;
        const isSelectDisabled =
          maxSelectedRows !== undefined && selectedRows.length >= maxSelectedRows;

        return (
          <Fragment key={key}>
            <StyledRow
              data-test={row['data-test'] || 'table-row'}
              isSelected={isSelected}
              key={key}
              onClick={() => onClick(row)}
              role="row"
              {...props}
            >
              {hasCheckboxColumn && (
                <TableCell data-test="table-row-checkbox-cell">
                  <Checkbox
                    isDisabled={isSelectDisabled && !isSelected}
                    forceMountIcon
                    label=""
                    status={isSelected}
                    setChecked={() => onSelectRow(row)}
                    data-test="table-row-checkbox"
                  />
                </TableCell>
              )}
              {columns.map(({ RowCell, ...col }) => (
                // @ts-ignore
                <RowCell
                  key={`${key}-cell-${col.value}`}
                  row={row}
                  isSelected={isSelected}
                  {...col}
                />
              ))}
            </StyledRow>
            {isCollapsible &&
              isExpanded &&
              childrenRows.map((childrenRow, i) => (
                <StyledChildrenRow
                  data-test="table-row"
                  role="row"
                  key={`${key}_child_${i}`}
                  isSelected={isSelected}
                  onClick={() => {
                    return onClick(row);
                  }}
                  {...props}
                >
                  {columns.map(({ ChildrenCell, ...col }) => (
                    // @ts-ignore
                    <ChildrenCell
                      key={`${key}-childCell-${col.value}`}
                      row={childrenRow}
                      isSelected={isSelected}
                      {...col}
                    />
                  ))}
                </StyledChildrenRow>
              ))}
          </Fragment>
        );
      })}
    </>
  );
};

export default TableRows;
